import { useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';

interface LogEvent {
  ts: number;
  message: string;
  status?: number;
  url?: string;
}

export function LogsPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!live) {
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(
      `${proto}//${window.location.host}/api/workspaces/${encodeURIComponent(id)}/logs/stream`,
    );
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data) as LogEvent;
        setEvents((prev) => [...prev.slice(-200), ev]);
      } catch {
        // ignore non-JSON heartbeats like 'pong'
      }
    };
    return () => ws.close();
  }, [live, id]);

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Logs</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
            />
            Live tail
          </label>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Live tail uses LogStreamDO via WebSocket. Paginated history is fetched from{' '}
          <code className="text-xs">/api/workspaces/:id/logs</code>.
        </p>
      </div>
      <div className="card overflow-x-auto">
        <table className="min-w-full text-xs font-mono">
          <thead>
            <tr className="border-b">
              <th className="px-2 py-1 text-left">ts</th>
              <th className="px-2 py-1 text-left">status</th>
              <th className="px-2 py-1 text-left">message</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-2 py-6 text-center text-slate-400">
                  No events yet. Turn on Live tail to stream.
                </td>
              </tr>
            ) : (
              events.map((e, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="px-2 py-1 text-slate-500">
                    {new Date(e.ts).toLocaleTimeString()}
                  </td>
                  <td className="px-2 py-1">{e.status ?? '—'}</td>
                  <td className="px-2 py-1">{e.message}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
